import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PawPrint, Send, CheckCircle, ClipboardList, FileText, Phone,
  ChevronRight, ArrowRight, Info
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntakeFormPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    owner_name: '',
    email: '',
    phone: '',
    dog_name: '',
    dog_breed: '',
    dog_age_months: '',
    dog_gender: '',
    household_members: '',
    other_pets: '',
    specific_behaviors: '',
    behavior_duration: '',
    prior_training: '',
    medical_notes: '',
    what_tried: '',
    goals: '',
    quiz_completed: false,
    preferred_contact_time: '',
    referral_source: '',
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.owner_name || !form.email || !form.phone || !form.dog_name || !form.dog_breed || !form.specific_behaviors) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!form.dog_age_months || parseInt(form.dog_age_months) < 1) {
      toast.error("Please enter your dog's age in months");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/intake`, {
        ...form,
        dog_age_months: parseInt(form.dog_age_months),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-edupaws-cream to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-edupaws-sage/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-edupaws-sage" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-edupaws-navy mb-3">Form Submitted</h1>
          <p className="font-body text-gray-500 mb-6">
            Thank you, {form.owner_name}. Your intake form for <strong>{form.dog_name}</strong> has been received.
            Stephanie will review it and reach out to schedule your phone intake call.
          </p>
          {!form.quiz_completed && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-700 font-body">
                  <p className="font-bold">Don't forget the Dog Psychology Quiz!</p>
                  <p className="mt-1">The quiz is required before your phone intake call.</p>
                  <button
                    onClick={() => navigate('/quiz')}
                    className="mt-2 inline-flex items-center gap-1 text-amber-800 font-bold hover:underline"
                  >
                    Take the Quiz <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 rounded-full bg-edupaws-navy text-white font-body font-bold hover:bg-edupaws-navy/90 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-edupaws-cream to-white">
      {/* Header */}
      <div className="bg-edupaws-navy text-white py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <PawPrint className="w-6 h-6 text-edupaws-sage" />
            <span className="font-heading text-lg font-bold">Edu Paws</span>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-4">Behavior Assessment Intake Form</h1>
          <p className="font-body text-white/70 mt-2 text-sm">
            Complete this form before your phone intake call with Stephanie.
            All information is confidential and helps prepare for your assessment.
          </p>
        </div>
      </div>

      {/* Intake Process Steps */}
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-4">
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: ClipboardList, label: 'Dog Psychology Quiz', status: 'Link below' },
            { icon: FileText, label: 'This Intake Form', status: 'Step 2 of 3' },
            { icon: Phone, label: 'Phone Intake Call', status: 'After form review' },
          ].map((step, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-3 text-center">
              <step.icon className="w-5 h-5 text-edupaws-sage mx-auto mb-1.5" />
              <p className="font-body font-bold text-xs text-edupaws-navy">{step.label}</p>
              <p className="font-body text-[10px] text-gray-400 mt-0.5">{step.status}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 pb-16">
        <form onSubmit={handleSubmit} className="space-y-8">

          {/* Owner Info */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-heading text-lg font-bold text-edupaws-navy mb-4">Your Information</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Full Name *</Label>
                <Input
                  data-testid="intake-owner-name"
                  value={form.owner_name}
                  onChange={(e) => handleChange('owner_name', e.target.value)}
                  placeholder="Your name"
                  required
                  className="h-11 rounded-xl border-gray-200 font-body"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Email *</Label>
                <Input
                  data-testid="intake-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="you@email.com"
                  required
                  className="h-11 rounded-xl border-gray-200 font-body"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Phone *</Label>
                <Input
                  data-testid="intake-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                  className="h-11 rounded-xl border-gray-200 font-body"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Best Time to Call</Label>
                <select
                  data-testid="intake-contact-time"
                  value={form.preferred_contact_time}
                  onChange={(e) => handleChange('preferred_contact_time', e.target.value)}
                  className="w-full h-11 rounded-xl border border-gray-200 px-3 font-body text-sm focus:outline-none focus:border-edupaws-sage"
                >
                  <option value="">Select preference</option>
                  <option value="morning">Morning (9am-12pm)</option>
                  <option value="afternoon">Afternoon (12pm-4pm)</option>
                  <option value="evening">Evening (4pm-7pm)</option>
                </select>
              </div>
            </div>
          </section>

          {/* Dog Info */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-heading text-lg font-bold text-edupaws-navy mb-4">About Your Dog</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Dog's Name *</Label>
                <Input
                  data-testid="intake-dog-name"
                  value={form.dog_name}
                  onChange={(e) => handleChange('dog_name', e.target.value)}
                  placeholder="e.g. Buddy"
                  required
                  className="h-11 rounded-xl border-gray-200 font-body"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Breed *</Label>
                <Input
                  data-testid="intake-dog-breed"
                  value={form.dog_breed}
                  onChange={(e) => handleChange('dog_breed', e.target.value)}
                  placeholder="e.g. German Shepherd"
                  required
                  className="h-11 rounded-xl border-gray-200 font-body"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Age (months) *</Label>
                <Input
                  data-testid="intake-dog-age"
                  type="number"
                  min="1"
                  max="240"
                  value={form.dog_age_months}
                  onChange={(e) => handleChange('dog_age_months', e.target.value)}
                  placeholder="e.g. 18"
                  required
                  className="h-11 rounded-xl border-gray-200 font-body"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Gender</Label>
                <select
                  data-testid="intake-dog-gender"
                  value={form.dog_gender}
                  onChange={(e) => handleChange('dog_gender', e.target.value)}
                  className="w-full h-11 rounded-xl border border-gray-200 px-3 font-body text-sm focus:outline-none focus:border-edupaws-sage"
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="male_neutered">Male (Neutered)</option>
                  <option value="female_spayed">Female (Spayed)</option>
                </select>
              </div>
            </div>
          </section>

          {/* Household */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-heading text-lg font-bold text-edupaws-navy mb-4">Household</h2>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Who lives in the home? (ages, roles)</Label>
                <textarea
                  data-testid="intake-household"
                  value={form.household_members}
                  onChange={(e) => handleChange('household_members', e.target.value)}
                  placeholder="e.g. 2 adults, 1 child (age 7), grandmother visits weekends"
                  rows={2}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 font-body text-sm focus:outline-none focus:border-edupaws-sage resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Other pets in the home?</Label>
                <Input
                  data-testid="intake-other-pets"
                  value={form.other_pets}
                  onChange={(e) => handleChange('other_pets', e.target.value)}
                  placeholder="e.g. 1 cat, no other dogs"
                  className="h-11 rounded-xl border-gray-200 font-body"
                />
              </div>
            </div>
          </section>

          {/* Behaviors & History */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-heading text-lg font-bold text-edupaws-navy mb-4">Behaviors & History</h2>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">What specific behaviors prompted this call? *</Label>
                <textarea
                  data-testid="intake-behaviors"
                  value={form.specific_behaviors}
                  onChange={(e) => handleChange('specific_behaviors', e.target.value)}
                  placeholder="Describe the behaviors you're seeing in detail..."
                  rows={4}
                  required
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 font-body text-sm focus:outline-none focus:border-edupaws-sage resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">How long has this been going on?</Label>
                <Input
                  data-testid="intake-duration"
                  value={form.behavior_duration}
                  onChange={(e) => handleChange('behavior_duration', e.target.value)}
                  placeholder="e.g. Started 6 months ago after we moved"
                  className="h-11 rounded-xl border-gray-200 font-body"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Prior training experience?</Label>
                <textarea
                  data-testid="intake-prior-training"
                  value={form.prior_training}
                  onChange={(e) => handleChange('prior_training', e.target.value)}
                  placeholder="e.g. Group puppy class at PetSmart, hired a private trainer for 3 sessions"
                  rows={2}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 font-body text-sm focus:outline-none focus:border-edupaws-sage resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">What have you already tried?</Label>
                <textarea
                  data-testid="intake-what-tried"
                  value={form.what_tried}
                  onChange={(e) => handleChange('what_tried', e.target.value)}
                  placeholder="Tools, methods, advice you've followed so far..."
                  rows={2}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 font-body text-sm focus:outline-none focus:border-edupaws-sage resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Any medical notes or medications?</Label>
                <textarea
                  data-testid="intake-medical"
                  value={form.medical_notes}
                  onChange={(e) => handleChange('medical_notes', e.target.value)}
                  placeholder="Known health conditions, medications, recent vet visits..."
                  rows={2}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 font-body text-sm focus:outline-none focus:border-edupaws-sage resize-none"
                />
              </div>
            </div>
          </section>

          {/* Goals */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-heading text-lg font-bold text-edupaws-navy mb-4">Your Goals</h2>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">What does success look like for you?</Label>
                <textarea
                  data-testid="intake-goals"
                  value={form.goals}
                  onChange={(e) => handleChange('goals', e.target.value)}
                  placeholder="Describe what you'd like your life with your dog to look like..."
                  rows={3}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 font-body text-sm focus:outline-none focus:border-edupaws-sage resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">How did you hear about Edu Paws?</Label>
                <Input
                  data-testid="intake-referral"
                  value={form.referral_source}
                  onChange={(e) => handleChange('referral_source', e.target.value)}
                  placeholder="e.g. Instagram, friend referral, Google"
                  className="h-11 rounded-xl border-gray-200 font-body"
                />
              </div>
            </div>
          </section>

          {/* Quiz Completion */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-heading text-lg font-bold text-edupaws-navy mb-4">Dog Psychology Quiz</h2>
            <p className="text-sm text-gray-500 font-body mb-4">
              The Dog Psychology Quiz must be completed before your phone intake call.
              It produces your dog's Nature type and helps Stephanie prepare for the assessment.
            </p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="intake-quiz-check"
                  type="checkbox"
                  checked={form.quiz_completed}
                  onChange={(e) => handleChange('quiz_completed', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-edupaws-sage focus:ring-edupaws-sage"
                />
                <span className="font-body font-bold text-sm text-edupaws-navy">I've completed the Dog Psychology Quiz</span>
              </label>
            </div>
            {!form.quiz_completed && (
              <button
                type="button"
                onClick={() => window.open('/quiz', '_blank')}
                data-testid="intake-quiz-link"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-edupaws-sage font-body font-bold hover:underline"
              >
                Take the Quiz now <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </section>

          {/* Submit */}
          <button
            type="submit"
            data-testid="intake-submit"
            disabled={submitting}
            className="w-full bg-edupaws-navy text-white py-4 rounded-xl font-body font-bold text-base
              shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all
              disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? 'Submitting...' : 'Submit Intake Form'}
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
